import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, BookOpen, ClipboardList, Eye, Landmark, LayoutDashboard, Map, Network, Send,
  SlidersHorizontal, Table2, Workflow
} from 'lucide-react';
import apiService from '../../services/apiService';

// Chart/diagram stroke constants — same convention as ForecastExecView.js.
// All panel backgrounds and text colors come from CSS variables (Cove/Meadow aware).
const GOLD = '#FFAC03';
const GREEN = '#2f9e6e';
const RED = '#d9534f';
const AMBER = '#e0a93c';
const BLUE = '#5b8dd6';

// ── Illustrative macro scenario sets (guide demo only) ───────────────────────
// Demo/fallback copies for this walkthrough — live values come from the cycle's
// central settings (OP_02X). Do not treat these as authoritative macro inputs.
const MACROS = {
  Base: { sofr: 4.85, gdp: 2.1, beta: 38, creditLoss: 0.42, runoff: 3.8, expInfl: 3.1, wageInfl: 3.8, demand: 1.0 },
  Upside: { sofr: 4.45, gdp: 2.8, beta: 34, creditLoss: 0.34, runoff: 2.9, expInfl: 2.6, wageInfl: 3.4, demand: 1.08 },
  Downside: { sofr: 3.85, gdp: 0.6, beta: 48, creditLoss: 0.78, runoff: 6.4, expInfl: 4.2, wageInfl: 4.2, demand: 0.86 }
};

const MACRO_FIELDS = [
  ['sofr', 'SOFR %'],
  ['gdp', 'GDP %'],
  ['beta', 'Deposit beta %'],
  ['creditLoss', 'Credit loss %'],
  ['runoff', 'Deposit runoff %'],
  ['expInfl', 'Expense infl %'],
  ['wageInfl', 'Wage infl %'],
  ['demand', 'Loan demand ×']
];

// Higher-is-favorable per metric, for coloring vs Base. null = directionless.
const GOOD_UP = {
  sofr: null, gdp: true, beta: false, creditLoss: false, runoff: false, expInfl: false, wageInfl: false, demand: true
};

// Illustrative replica of buildMonthlyProjection on a $1.0B-loan / $1.3B-deposit unit.
const runModel = (m) => {
  let loans = 1000;
  let deposits = 1300;
  const rows = [];
  const baseYield = 6.48 + (m.sofr - 4.85) * 0.58; // loan repricing beta 58
  const baseCost = 2.47 + (m.sofr - 4.85) * (m.beta / 100);
  const loanGrowth = Math.min(8.5, 3.2 + m.gdp * 1.4 * m.demand); // limiter 8.5
  const depGrowth = Math.max(0.5, 3.0 + m.gdp * 0.9 - m.runoff * 0.35);
  for (let i = 0; i < 60; i += 1) {
    const yi = Math.floor(i / 12);
    const season = Math.sin(((i % 12) / 12) * Math.PI * 2) * 0.08;
    loans *= 1 + (loanGrowth + season) / 100 / 12;
    deposits *= 1 + (depGrowth - season * 0.6) / 100 / 12;
    const ly = Math.min(9.75, Math.max(3.5, baseYield + yi * 0.035 + season * 0.02));
    const dc = Math.max(0.15, baseCost + yi * 0.015);
    const fade = Math.max(0.35, 1 - yi * 0.08);
    const nii = (loans * (ly / 100)) / 12 - (deposits * (dc / 100)) / 12 + 0.55 * fade;
    rows.push({ loans, nii });
  }
  return rows;
};

const MiniChart = ({ baseRows, scenRows, field, format, isBase }) => {
  const all = [...baseRows, ...scenRows].map((row) => row[field]);
  let min = Math.min(...all);
  let max = Math.max(...all);
  const pad = (max - min) * 0.12 || 1;
  min -= pad;
  max += pad;
  const toPath = (rows) =>
    rows
      .map((row, i) => {
        const x = 18 + (i / (rows.length - 1)) * 424;
        const y = 164 - ((row[field] - min) / (max - min)) * 148;
        return `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(' ');
  return (
    <svg className="forecast-guide-svg" viewBox="0 0 460 180" role="img" aria-label="Scenario chart">
      <line x1="18" y1="164" x2="442" y2="164" className="gaxis" />
      <text x="18" y="176" className="gmuted" fontSize="9">M1</text>
      <text x="430" y="176" className="gmuted" fontSize="9">M60</text>
      <text x="18" y="12" className="gmuted" fontSize="9">{format(max)}</text>
      <text x="18" y="160" className="gmuted" fontSize="9">{format(min)}</text>
      <path d={toPath(baseRows)} className="gstroke-muted" strokeWidth="1.4" strokeDasharray="5 3" />
      <path
        d={toPath(scenRows)}
        fill="none"
        className={isBase ? 'gstroke-muted' : undefined}
        stroke={isBase ? undefined : GOLD}
        strokeWidth="2.2"
      />
    </svg>
  );
};

// ── Walkthrough cards (OP codes) ─────────────────────────────────────────────
const OP_CARDS = [
  { op: 'OP_01', title: 'Base forecast', body: "Your unit's baseline before any overrides: monthly matrices and annual charts seeded from the unit's own GL actuals (Versions=Actual, your planning entity only)." },
  { op: 'OP_02', title: 'Macro environment', body: 'Central base vs shock paths — SOFR, GDP, deposit beta, credit loss, runoff, expense/wage inflation. Pick Base / Upside / Downside or add custom shocks. See the propagation section below for exactly where each one lands.' },
  { op: 'OP_03', title: 'Baseline adjustments', body: 'Per-year levers (growth pts, fee/expense/comp/provision $MM), driver-based fees (bps on balances or production), workforce-year levers, trend review and the off-trend radar.' },
  { op: 'OP_04', title: 'Producer hiring', body: 'Hire cohorts by officer type: FTE, start month, ramp, vacancy and attrition overrides, per-year production economics. Production flows into balances, NII, and fees monthly.' },
  { op: 'OP_04B', title: 'Portfolio & blend', body: 'Going-on vs going-off volume and rates; manual vs statistical fade across the horizon; product mix by banker type.' },
  { op: 'OP_05', title: 'Results', body: 'Baseline vs modified plan with cumulative waterfalls — NII/volume, fees, expense control, credit, and tax bridges per year.' },
  { op: 'OP_06', title: 'Constraint monitor', body: 'Bullet-range rows with Healthy / Watch / Breach bands: LCR, CET1, leverage, RBC, loan-to-deposit, wholesale funding, CRE concentration, growth vs appetite.' },
  { op: 'OP_07', title: 'Model validation', body: 'Calculation-path checks and blocked-row screening. Blocked rows stop submission — fix them here first.' },
  { op: 'OP_08', title: 'Forecast load center', body: 'Stage → validate → approve → write → reconcile. Shows the exact monthly cube rows (~1,020 = 17 GL lines × 60 months) your package will load.' }
];

// ── Macro propagation map data ───────────────────────────────────────────────
const PROP_DRIVERS = [
  'SOFR + rate shock', 'Deposit beta', 'GDP / loan demand', 'Credit loss',
  'Deposit runoff', 'Expense inflation', 'Wage inflation', 'Prepayment'
];
const PROP_DRIVER_Y = [34, 72, 110, 148, 186, 224, 262, 300];
const PROP_MECHANISMS = [
  'loan yield & deposit cost paths (clamped)',
  'balance growth × seasonality × year fade',
  'provision scaling + hire-production haircut',
  'funding benefit fade + liquidity guardrails',
  'NIE / comp inflation factors',
  'going-off book runoff (NII buildup)'
];
const PROP_MECH_Y = [34, 84, 134, 184, 234, 284];
const PROP_TARGETS = [
  '419 Int. income · 459 Int. expense',
  '4290 Funding benefit (FTP)',
  '139 Loans · 219/229/239 Deposits',
  '850 Provision',
  'OP_06 liquidity guardrails',
  '609–789 NIE lines',
  '609 Comp · 619 Benefits',
  '529 Fees · NII mix'
];
const PROP_TARGET_Y = [34, 66, 98, 130, 162, 194, 226, 258];
const PROP_LEFT_PATHS = [
  'M146 49 H284',
  'M146 87 Q220 60 284 52',
  'M146 125 Q215 110 284 100',
  'M146 163 H284 V152',
  'M146 201 H284',
  'M146 239 Q215 245 284 249',
  'M146 277 Q215 260 284 252',
  'M146 313 Q215 305 284 300'
];
const PROP_RIGHT_PATHS = [
  'M590 49 H784',
  'M590 55 Q690 70 784 78',
  'M590 99 Q690 105 784 110',
  'M590 149 H784 V144',
  'M590 199 Q690 188 784 176',
  'M590 249 Q690 235 784 240 M590 249 Q690 220 784 208',
  'M590 299 Q690 285 784 272'
];

const ENGINE_FORMULAS = `ending_balance[m]   = balance[m-1] × (1 + (annual_growth + seasonality(m)) / 100 / 12)     // balances ROLL FORWARD
interest_income[m]  = avg_loans[m]    × loan_yield[m] / 100 / 12                          // flows ACCUMULATE
interest_expense[m] = avg_deposits[m] × deposit_cost[m] / 100 / 12
loan_yield[m]       = clamp(base_yield + year_index×0.035 + seasonality×0.02, 3.5, 9.75)   // macro SOFR shifts base
deposit_cost[m]     = max(floor 0.15, base_cost + year_index×0.015 + beta-driven shocks)
nii[m]              = interest_income − interest_expense + funding_benefit(fade ≥ 0.35)    // FB loads as acct 4290
nie[m]              = (annual_NIE / 12) × (1 + year_index × expense_inflation × 0.36)
provision[m]        = (annual_prov / 12) × (1 + year_index × max(0, credit_loss − base) × 0.18)
hire_production[m]  = FTE × (1−vacancy) × survival(attrition) × ramp × demand(GDP) × credit_haircut(credit_loss)`;

// ── Driver → line-item matrix ────────────────────────────────────────────────
const MATRIX_COLUMNS = [
  { key: 'loans', label: 'Loans', sub: '139 · EOP' },
  { key: 'deposits', label: 'Deposits', sub: '219/229/239 · EOP' },
  { key: 'intInc', label: 'Int. income', sub: '419 · MTD' },
  { key: 'fundBen', label: 'Funding benefit', sub: '4290 · MTD' },
  { key: 'intExp', label: 'Int. expense', sub: '459 · MTD' },
  { key: 'fees', label: 'Fees / NIR', sub: '529 · MTD' },
  { key: 'nie', label: 'NIE', sub: '609–789 · MTD' },
  { key: 'provision', label: 'Provision', sub: '850 · MTD' },
  { key: 'tax', label: 'Tax', sub: '909 · MTD' }
];

const D = (note) => ({ type: 'direct', note });
const I = (note) => ({ type: 'indirect', note });

const MATRIX_ROWS = [
  { driver: 'SOFR + rate shock', where: 'OP_02', cells: { intInc: D(), fundBen: D(), intExp: D(), tax: I() } },
  { driver: 'Deposit beta shock', where: 'OP_02', cells: { intExp: D(), tax: I() } },
  { driver: 'GDP / loan demand', where: 'OP_02', cells: { loans: D(), deposits: D(), intInc: I(), fundBen: I(), intExp: I(), tax: I() } },
  { driver: 'Credit loss / stress bps', where: 'OP_02', cells: { loans: I('hire haircut'), provision: D(), tax: I() } },
  { driver: 'Deposit runoff', where: 'OP_02; liquidity', cells: { deposits: D(), fundBen: I(), intExp: I() } },
  { driver: 'Expense inflation', where: 'OP_02', cells: { nie: D(), tax: I() } },
  { driver: 'Wage inflation', where: 'OP_02 → comp lines', cells: { nie: D('609/619'), tax: I() } },
  { driver: 'Prepayment', where: 'OP_02 → OP_04B runoff', cells: { loans: D(), intInc: I() } },
  { driver: 'Loan growth adj (pts/yr)', where: 'OP_03Y', cells: { loans: D(), intInc: I(), tax: I() } },
  { driver: 'Deposit growth adj (pts/yr)', where: 'OP_03Y', cells: { deposits: D(), intExp: I(), tax: I() } },
  { driver: 'Other NIR adj ($MM/yr)', where: 'OP_03Y', cells: { fees: D(), tax: I() } },
  { driver: 'Operating expense adj ($MM/yr)', where: 'OP_03Y', cells: { nie: D(), tax: I() } },
  { driver: 'Compensation adj ($MM/yr)', where: 'OP_03Y', cells: { nie: D('609'), tax: I() } },
  { driver: 'Provision adj ($MM/yr)', where: 'OP_03Y', cells: { provision: D(), tax: I() } },
  { driver: 'Treasury mgmt fees', where: 'OP_03L · bps × avg deposits', cells: { deposits: I('basis'), fees: D(), tax: I() } },
  { driver: 'Commercial banking fees', where: 'OP_03L · bps × new loan production', cells: { loans: I('basis'), fees: D(), tax: I() } },
  { driver: 'Wealth / trust fees', where: 'OP_03L · bps × avg loans', cells: { loans: I('basis'), fees: D(), tax: I() } },
  { driver: 'Merit / incentive accrual', where: 'OP_03W per year', cells: { nie: D('609/619'), tax: I() } },
  { driver: 'Banker productivity lift', where: 'OP_03W → growth pts', cells: { loans: D(), deposits: D(), intInc: I(), intExp: I(), tax: I() } },
  { driver: 'Loan / deposit retention', where: 'OP_03W → growth pts', cells: { loans: D(), deposits: D(), intInc: I(), intExp: I(), tax: I() } },
  { driver: 'Hire cohorts', where: 'OP_04 · FTE, ramp, vacancy, attrition, per-year production', cells: { loans: D(), deposits: D(), intInc: I(), intExp: I(), fees: D(), nie: D(), tax: I() } },
  { driver: 'HR roster actions', where: 'OP_03W · terminate / transfer / retain', cells: { nie: D('609/619'), tax: I() } },
  { driver: 'Central items', where: 'FDIC bps, FF&E %, tax rate — Central FP&A', cells: { nie: D(), tax: D() } }
];

const MatrixCell = ({ cell }) => {
  if (!cell) return <td className="c" />;
  return (
    <td className="c">
      <span className={cell.type === 'direct' ? 'forecast-guide-dot-direct' : 'forecast-guide-dot-indirect'}>
        {cell.type === 'direct' ? '●' : '○'}
      </span>
      {cell.note ? <span className="cell-note">{cell.note}</span> : null}
    </td>
  );
};

const ForecastGuide = () => {
  const [scenario, setScenario] = useState('Base');
  const [centralLink, setCentralLink] = useState(null);

  // One-click deep link into the first package's input page (OP_02X lives there).
  useEffect(() => {
    let cancelled = false;
    const resolveLink = async () => {
      try {
        const result = await apiService.fetchForecastSubmissions('FY2027_BUDGET');
        const firstId = result?.submissions?.[0]?.id;
        if (!cancelled) setCentralLink(firstId ? `/forecasting/input/${firstId}` : '/forecasting');
      } catch (err) {
        if (!cancelled) setCentralLink('/forecasting');
      }
    };
    resolveLink();
    return () => {
      cancelled = true;
    };
  }, []);

  const macro = MACROS[scenario];
  const baseRows = useMemo(() => runModel(MACROS.Base), []);
  const scenRows = useMemo(() => (scenario === 'Base' ? baseRows : runModel(MACROS[scenario])), [scenario, baseRows]);

  const macroTiles = MACRO_FIELDS.map(([key, label]) => {
    const value = macro[key];
    const baseValue = MACROS.Base[key];
    let tone = '';
    if (value !== baseValue && GOOD_UP[key] !== null) {
      tone = (value > baseValue) === GOOD_UP[key] ? 'up' : 'down';
    }
    return { key, label, value, tone };
  });

  return (
    <div className="forecast-module forecast-guide">
      <section className="forecast-command">
        <div>
          <div className="forecast-kicker">
            <BookOpen size={14} />
            Forecast Console / How-to guide
          </div>
          <h1>How the forecast module works — and how to drive it</h1>
          <p>
            A bottoms-up, FP&A-governed forecasting workflow: each division-market planning unit starts from its own
            GL actuals, layers the central macro scenario and its local levers on a live 60-month engine, and submits a
            package that FP&A reviews, approves, and loads to the forecast cube. Every console figure is traceable back
            to these inputs.
          </p>
          <div className="forecast-guide-chips">
            <span className="forecast-health-chip green">70 planning units · 7 divisions × 10 markets</span>
            <span className="forecast-health-chip neutral">60-month horizon · FY2027–FY2031</span>
            <span className="forecast-health-chip neutral">Grain: Region + Market planning entity</span>
            <span className="forecast-health-chip green">All write-back is FP&A-governed</span>
          </div>
        </div>
        <div className="forecast-command-grid">
          <div className="forecast-home-links">
            <Link className="forecast-home-link" to="/forecasting">
              <ClipboardList size={14} /> Cycle home
            </Link>
            <Link className="forecast-home-link" to="/forecasting/review">
              <LayoutDashboard size={14} /> Review console
            </Link>
            <Link className="forecast-home-link" to="/forecasting/exec">
              <Eye size={14} /> Executive view
            </Link>
          </div>
        </div>
      </section>

      {/* ── GD_00 · Module walkthrough video ── */}
      <section className="forecast-panel forecast-guide-video">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">GD_00 ● Module walkthrough</span>
          <h2>
            <BookOpen size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Watch the module walkthrough
          </h2>
        </div>
        <video
          className="forecast-guide-video-player"
          src={`${process.env.PUBLIC_URL}/media/forecasting-module-launch.mp4`}
          poster={`${process.env.PUBLIC_URL}/media/forecasting-module-launch-poster.jpg`}
          controls
          preload="metadata"
          playsInline
          style={{ width: '100%', borderRadius: 14, display: 'block', background: '#000' }}
        >
          Your browser does not support embedded video.{' '}
          <a href={`${process.env.PUBLIC_URL}/media/forecasting-module-launch.mp4`}>Download the walkthrough</a>.
        </video>
      </section>

      {/* ── GD_00B · The complete cycle ── */}
      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">GD_00B ● The complete cycle</span>
          <h2>
            <Network size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            The end-to-end forecast cycle, role by role
          </h2>
        </div>
        <p className="forecast-guide-copy">
          The whole cycle narrows as it flows: Central FP&A <b>publishes</b> one macro base that <b>anchors</b> every
          unit&apos;s driver-view; <b>many inputters</b> then plan against that anchor in parallel; their packages
          <b> converge</b> onto a handful of FP&A managers who return or approve; and everything <b>rolls up</b> into a
          single consolidated load the executives read. Colour marks the role; the heavier gold crossings are the
          ownership hand-offs; the red dashed line is the manager&apos;s return-with-comment.
        </p>

        <svg className="forecast-guide-svg forecast-guide-cycle" viewBox="0 0 1000 560" role="img" aria-label="End-to-end forecast cycle swimlane">
          <title>End-to-end regional bank forecast cycle</title>
          <desc>
            Five phases flow left to right across four role lanes — Corporate FP&amp;A pre-seeds the macro and anchors a
            baseline, many inputters plan in parallel, FP&amp;A managers review and gate, and Consolidated FP&amp;A plus
            executives roll up and read. Many inputter nodes converge to fewer review nodes to one consolidated node.
          </desc>
          <defs>
            <marker id="cy-seq" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
              <path d="M0 0L8 4L0 8z" className="gmuted" />
            </marker>
            <marker id="cy-hand" viewBox="0 0 9 9" refX="8" refY="4.5" markerWidth="8" markerHeight="8" orient="auto">
              <path d="M0 0L9 4.5L0 9z" fill={GOLD} />
            </marker>
            <marker id="cy-back" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
              <path d="M0 0L8 4L0 8z" fill={RED} />
            </marker>
          </defs>

          {/* lane bands */}
          <g>
            <rect x="132" y="34" width="856" height="118" className="gcycle-lane" />
            <rect x="132" y="158" width="856" height="150" className="gcycle-lane alt" />
            <rect x="132" y="314" width="856" height="104" className="gcycle-lane" />
            <rect x="132" y="424" width="856" height="104" className="gcycle-lane alt" />
          </g>

          {/* lane labels (left) */}
          <g fontSize="10.5" textAnchor="start">
            <rect x="10" y="34" width="116" height="118" rx="9" className="gnode" />
            <text x="20" y="82" className="gtext">Corporate</text>
            <text x="20" y="97" className="gtext">FP&A</text>
            <text x="20" y="116" className="gmuted" fontSize="9">pre-seed &amp; anchor</text>

            <rect x="10" y="158" width="116" height="150" rx="9" className="gnode" />
            <text x="20" y="220" className="gtext">Inputters</text>
            <text x="20" y="235" className="gmuted" fontSize="9">many, in</text>
            <text x="20" y="247" className="gmuted" fontSize="9">parallel</text>

            <rect x="10" y="314" width="116" height="104" rx="9" className="gnode" />
            <text x="20" y="360" className="gtext">FP&A</text>
            <text x="20" y="375" className="gtext">Managers</text>
            <text x="20" y="393" className="gmuted" fontSize="9">review &amp; gate</text>

            <rect x="10" y="424" width="116" height="104" rx="9" className="gnode" />
            <text x="20" y="466" className="gtext">Consolidated</text>
            <text x="20" y="481" className="gtext">+ Execs</text>
            <text x="20" y="499" className="gmuted" fontSize="9">roll up &amp; read</text>
          </g>

          {/* phase headers (top) */}
          <g fontSize="9.5" textAnchor="middle" fill={GOLD}>
            <text x="228" y="26">1 · PRE-SEED</text>
            <text x="418" y="26">2 · PARALLEL INPUT</text>
            <text x="600" y="26">3 · NEW PLANS</text>
            <text x="770" y="26">4 · REVIEW</text>
            <text x="922" y="26">5 · ROLLUP</text>
          </g>

          {/* ── edges (drawn before nodes so nodes sit on top) ── */}
          {/* Phase 1 sequence: macro → baseline */}
          <path d="M300 74 H346" className="gline" markerEnd="url(#cy-seq)" />
          {/* anchor hand-off: baseline fans out to the 3 parallel inputter nodes */}
          <path d="M424 96 Q470 130 500 196" className="gcycle-hand" markerEnd="url(#cy-hand)" />
          <path d="M424 96 Q470 170 500 238" className="gcycle-hand" markerEnd="url(#cy-hand)" />
          <path d="M424 96 Q470 210 500 280" className="gcycle-hand" markerEnd="url(#cy-hand)" />
          <text x="452" y="150" fill={GOLD} fontSize="9" textAnchor="middle">anchors</text>

          {/* Phase 2 → Phase 3 sequence: existing book feeds the new-plan pair */}
          <path d="M632 210 Q672 210 700 232" className="gline" markerEnd="url(#cy-seq)" />
          <path d="M632 266 Q672 266 700 288" className="gline" markerEnd="url(#cy-seq)" />
          <text x="668" y="248" className="gmuted" fontSize="9" textAnchor="middle">adds growth</text>

          {/* Phase 2 + 3 submit hand-off: converge (many → fewer) onto managers */}
          <path d="M600 288 Q660 330 726 356" className="gcycle-hand" markerEnd="url(#cy-hand)" />
          <path d="M802 264 Q820 320 810 342" className="gcycle-hand" markerEnd="url(#cy-hand)" />
          <path d="M500 288 Q560 350 726 370" className="gcycle-hand" markerEnd="url(#cy-hand)" />
          <text x="700" y="336" fill={GOLD} fontSize="9" textAnchor="middle">submit</text>

          {/* Phase 4 feedback: manager returns w/ comment to inputters */}
          <path d="M726 388 Q520 470 500 306" fill="none" stroke={RED} strokeDasharray="5 3" markerEnd="url(#cy-back)" />
          <text x="560" y="452" fill={RED} fontSize="9" textAnchor="middle">return w/ comment</text>

          {/* Phase 4 → 5 approve + load hand-off: fewer → one */}
          <path d="M840 366 Q892 400 916 452" className="gcycle-hand" markerEnd="url(#cy-hand)" />
          <text x="900" y="416" fill={GOLD} fontSize="9" textAnchor="middle">approve · load</text>

          {/* Phase 5 sequence: consolidated → exec read */}
          <path d="M872 476 H894" className="gline" markerEnd="url(#cy-seq)" />
          <text x="884" y="468" className="gmuted" fontSize="9" textAnchor="middle">read</text>

          {/* ── nodes ── */}
          <g fontSize="10.5" textAnchor="middle">
            {/* Lane 1 — Corporate FP&A */}
            <rect x="150" y="58" width="150" height="34" rx="9" className="gnode-fill" stroke={GOLD} />
            <text x="225" y="79" fill={GOLD}>Publish macro paths</text>
            <text x="225" y="112" className="gmuted" fontSize="8.5">Base / Downside / Upside · versioned</text>
            <rect x="346" y="58" width="150" height="34" rx="9" className="gnode-fill" stroke={GOLD} />
            <text x="421" y="74" fill={GOLD}>Driver-based</text>
            <text x="421" y="87" fill={GOLD}>baseline anchor</text>

            {/* Lane 2 — Inputters (3 parallel, phase 2) */}
            <rect x="500" y="180" width="132" height="32" rx="9" className="gnode-fill" stroke={BLUE} />
            <text x="566" y="200" className="gtext">Baseline review</text>
            <rect x="500" y="224" width="132" height="32" rx="9" className="gnode-fill" stroke={BLUE} />
            <text x="566" y="244" className="gtext">Workforce plan</text>
            <rect x="500" y="268" width="132" height="32" rx="9" className="gnode-fill" stroke={BLUE} />
            <text x="566" y="288" className="gtext">Project roll-forward</text>

            {/* Lane 2 — Inputters (paired new plans, phase 3) */}
            <rect x="700" y="222" width="132" height="32" rx="9" className="gnode-fill" stroke={GREEN} />
            <text x="766" y="242" className="gtext">New-hire cohorts</text>
            <rect x="700" y="266" width="132" height="32" rx="9" className="gnode-fill" stroke={GREEN} />
            <text x="766" y="286" className="gtext">New projects</text>

            {/* Lane 3 — FP&A Managers (fewer) */}
            <rect x="726" y="340" width="148" height="34" rx="9" className="gnode-fill" stroke={AMBER} />
            <text x="800" y="356" fill={AMBER}>Review + commentary</text>
            <text x="800" y="369" fill={AMBER} fontSize="8.5">variance vs driver-view gate</text>

            {/* Lane 4 — Consolidated + Execs (one) */}
            <rect x="726" y="450" width="146" height="34" rx="9" className="gnode-fill" stroke={GREEN} />
            <text x="799" y="466" fill={GREEN}>Consolidated rollup</text>
            <text x="799" y="479" fill={GREEN} fontSize="8.5">load to cube · reconcile</text>
            <rect x="894" y="450" width="94" height="34" rx="9" className="gnode" />
            <text x="941" y="466" className="gtext">Executive</text>
            <text x="941" y="479" className="gmuted" fontSize="8.5">read-only</text>
          </g>

          {/* edge-type legend */}
          <g fontSize="9" transform="translate(150,540)">
            <line x1="0" y1="0" x2="26" y2="0" className="gline" markerEnd="url(#cy-seq)" />
            <text x="32" y="3" className="gmuted">sequence</text>
            <line x1="130" y1="0" x2="156" y2="0" className="gcycle-hand" markerEnd="url(#cy-hand)" />
            <text x="162" y="3" fill={GOLD}>hand-off (governance)</text>
            <line x1="330" y1="0" x2="356" y2="0" stroke={RED} strokeDasharray="5 3" markerEnd="url(#cy-back)" />
            <text x="362" y="3" fill={RED}>return / feedback</text>
          </g>
        </svg>

        <div className="forecast-guide-note">
          What to notice: the flow gets <b>narrower</b> at every stage — many inputters → a few managers → one
          consolidated load — and the <b>hand-offs (gold) are where governance bites</b>: the anchor fixes everyone to
          one baseline, submit hands accountability to a manager, and approve/load is the only path to the cube. The red
          return loop is the sole way work flows backward — always with a comment and a full audit trail.
        </div>
      </section>

      {/* ── GD_01 · User journey ── */}
      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">GD_01 ● The user journey</span>
          <h2>
            <Workflow size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Four roles, one package lifecycle
          </h2>
        </div>
        <p className="forecast-guide-copy">
          <b>Finance liaisons</b> own a unit's inputs and submit its package. <b>FP&A managers</b> review, return or
          approve, and perform the only writes to the forecast cube. <b>Central FP&A</b> owns the macro base and central
          items every unit inherits. <b>Executives</b> read the consolidated review console — read-only by design.
        </p>

        <svg className="forecast-guide-svg" viewBox="0 0 1000 190" role="img" aria-label="Package lifecycle">
          <defs>
            <marker id="fg-arr" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
              <path d="M0 0L8 4L0 8z" className="gmuted" />
            </marker>
            <marker id="fg-arr-red" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
              <path d="M0 0L8 4L0 8z" fill={RED} />
            </marker>
          </defs>
          <g fontSize="12" textAnchor="middle">
            <rect x="14" y="58" width="142" height="44" rx="11" className="gnode" />
            <text x="85" y="84" className="gmuted">NOT STARTED</text>
            <rect x="206" y="58" width="142" height="44" rx="11" className="gnode-fill" stroke={AMBER} />
            <text x="277" y="84" fill={AMBER}>IN PROGRESS</text>
            <rect x="398" y="58" width="142" height="44" rx="11" className="gnode-fill" stroke={GOLD} />
            <text x="469" y="84" fill={GOLD}>SUBMITTED</text>
            <rect x="590" y="58" width="142" height="44" rx="11" className="gnode-fill" stroke={GREEN} />
            <text x="661" y="84" fill={GREEN}>APPROVED</text>
            <rect x="782" y="58" width="142" height="44" rx="11" className="gnode-fill" stroke={GREEN} />
            <text x="853" y="84" fill={GREEN}>LOADED</text>
            <line x1="156" y1="80" x2="200" y2="80" className="gline" markerEnd="url(#fg-arr)" />
            <line x1="348" y1="80" x2="392" y2="80" className="gline" markerEnd="url(#fg-arr)" />
            <line x1="540" y1="80" x2="584" y2="80" className="gline" markerEnd="url(#fg-arr)" />
            <line x1="732" y1="80" x2="776" y2="80" className="gline" markerEnd="url(#fg-arr)" />
            <text x="178" y="70" className="gmuted" fontSize="10.5">save draft</text>
            <text x="370" y="70" className="gmuted" fontSize="10.5">submit</text>
            <text x="562" y="70" className="gmuted" fontSize="10.5">approve</text>
            <text x="754" y="70" className="gmuted" fontSize="10.5">FP&A load</text>
            <path d="M 440 102 Q 360 150 305 104" fill="none" stroke={RED} markerEnd="url(#fg-arr-red)" />
            <text x="372" y="146" fill={RED} fontSize="10.5">recall (liaison) / return (FP&A, comment)</text>
            <path d="M 845 102 Q 600 185 290 104" fill="none" stroke={RED} strokeDasharray="4 3" markerEnd="url(#fg-arr-red)" />
            <text x="585" y="172" fill={RED} fontSize="10.5">reopen (FP&A, comment required — full audit trail)</text>
          </g>
        </svg>

        <div className="forecast-guide-note">
          Guardrails baked into the lifecycle: a package with <b>blocked validation rows</b> cannot be submitted; red
          constraint breaches require an <b>override note</b> to approve; market users never write to the cube — the{' '}
          <b>FP&A manager performs the load</b> from the review console after approval; every transition is recorded
          with actor, comment, and timestamp in the submission event log.
        </div>
      </section>

      {/* ── GD_02 · Walkthrough ── */}
      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">GD_02 ● Walkthrough — one scrolling workflow</span>
          <h2>
            <Map size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            The forecast page, section by section
          </h2>
        </div>
        <p className="forecast-guide-copy">
          Open your unit from <i>Cycle home → your package</i>. The page is one long governed workflow; the sticky bar
          tracks scenario, sections, source status, and constraint health as you scroll. Everything recalculates live on
          every input.
        </p>
        <div className="forecast-guide-grid">
          {OP_CARDS.map((card) => (
            <div className="forecast-guide-card" key={card.op}>
              <span className="op">{card.op}</span>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </div>
          ))}
        </div>
        <p className="forecast-guide-copy" style={{ marginTop: 12 }}>
          After submission, your package appears on the <b>FP&A Review Console</b> (status board, trends, AI variance
          reads, consolidation flash) and rolls into the <b>Executive Review Console</b> (plan bridges, working-plan
          overlay, guardrails). Corporate initiatives live separately in the <b>Corporate Project Console</b> and load
          under their own PROJECT_* versions.
        </p>
      </section>

      {/* ── GD_03 · Macro scenario propagation ── */}
      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">GD_03 ● Macro scenario propagation</span>
          <h2>
            <Activity size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            One scenario, every line — where the macro actually lands
          </h2>
        </div>
        <p className="forecast-guide-copy">
          The macro set you choose in OP_02 (plus any custom shocks) is not a side-table: it is multiplied and added
          directly into the 60-month engine. Pick a scenario to see the governed values and an illustrative unit re-run
          live:
        </p>

        <div className="forecast-segmented" role="group" aria-label="Macro scenario picker">
          {Object.keys(MACROS).map((name) => (
            <button
              key={name}
              type="button"
              className={scenario === name ? 'active' : ''}
              onClick={() => setScenario(name)}
            >
              {name}
            </button>
          ))}
        </div>
        <p className="forecast-guide-copy" style={{ marginTop: '0.75rem', fontStyle: 'italic' }}>
          Illustrative defaults — live values come from the cycle&apos;s central settings
        </p>

        <div className="forecast-guide-vals">
          {macroTiles.map((tile) => (
            <div className="mv" key={tile.key}>
              <span>{tile.label}</span>
              <b className={tile.tone}>{tile.value}</b>
            </div>
          ))}
        </div>

        <svg className="forecast-guide-svg" viewBox="0 0 1000 330" role="img" aria-label="Macro propagation map">
          <defs>
            <marker id="fg-arr-blue" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6.5" markerHeight="6.5" orient="auto">
              <path d="M0 0L8 4L0 8z" fill={BLUE} />
            </marker>
          </defs>
          <g fontSize="11">
            <text x="80" y="20" fill={GOLD} textAnchor="middle">MACRO DRIVER</text>
            <text x="440" y="20" fill={GOLD} textAnchor="middle">MODEL MECHANISM (monthly engine)</text>
            <text x="880" y="20" fill={GOLD} textAnchor="middle">LINE ITEMS HIT</text>
            <g textAnchor="middle">
              {PROP_DRIVERS.map((label, idx) => (
                <g key={label}>
                  <rect x="14" y={PROP_DRIVER_Y[idx]} width="132" height={idx === PROP_DRIVERS.length - 1 ? 26 : 30} rx="9" className="gnode" />
                  <text x="80" y={PROP_DRIVER_Y[idx] + (idx === PROP_DRIVERS.length - 1 ? 17 : 19)} className="gtext">{label}</text>
                </g>
              ))}
            </g>
            <g textAnchor="middle">
              {PROP_MECHANISMS.map((label, idx) => (
                <g key={label}>
                  <rect x="290" y={PROP_MECH_Y[idx]} width="300" height="30" rx="9" className="gnode" />
                  <text x="440" y={PROP_MECH_Y[idx] + 19} className="gmuted">{label}</text>
                </g>
              ))}
            </g>
            <g fill="none" opacity="0.85">
              {PROP_LEFT_PATHS.map((d) => (
                <path key={d} d={d} stroke={BLUE} markerEnd="url(#fg-arr-blue)" />
              ))}
            </g>
            <g textAnchor="middle" fontSize="10.5">
              {PROP_TARGETS.map((label, idx) => (
                <g key={label}>
                  <rect x="790" y={PROP_TARGET_Y[idx]} width="180" height="26" rx="9" className="gnode-fill" stroke={GOLD} strokeOpacity="0.45" />
                  <text x="880" y={PROP_TARGET_Y[idx] + 17} fill={GOLD}>{label}</text>
                </g>
              ))}
            </g>
            <g opacity="0.6">
              {PROP_RIGHT_PATHS.map((d) => (
                <path key={d} d={d} className="gline" markerEnd="url(#fg-arr-blue)" />
              ))}
            </g>
          </g>
        </svg>

        <h3 className="forecast-guide-subhead">The actual formulas (monthly engine)</h3>
        <div className="forecast-guide-formula">{ENGINE_FORMULAS}</div>

        <h3 className="forecast-guide-subhead">See it move — illustrative $1.0B-loan unit, 60 months</h3>
        <p className="forecast-guide-copy">
          Directional replica of the engine above so you can see the scenario's shape; the governed numbers live in the
          app (OP_02 inherits the central macro base saved by Central FP&A).
        </p>
        <div className="forecast-guide-grid two">
          <div className="forecast-guide-card">
            <h3>Ending loans ($MM, EOP)</h3>
            <MiniChart
              baseRows={baseRows}
              scenRows={scenRows}
              field="loans"
              format={(v) => `$${Math.round(v)}`}
              isBase={scenario === 'Base'}
            />
          </div>
          <div className="forecast-guide-card">
            <h3>Monthly NII ($MM, MTD)</h3>
            <MiniChart
              baseRows={baseRows}
              scenRows={scenRows}
              field="nii"
              format={(v) => `$${v.toFixed(1)}`}
              isBase={scenario === 'Base'}
            />
          </div>
        </div>
        <div className="forecast-guide-legend">
          <span>— Base (reference, dashed)</span>
          <span style={{ color: scenario === 'Base' ? undefined : GOLD }}>
            — {scenario}
            {scenario === 'Base' ? ' (= reference)' : ''}
          </span>
        </div>
      </section>

      {/* ── GD_04 · Centrally-loaded assumptions ── */}
      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">GD_04 ● Centrally-loaded assumptions</span>
          <h2>
            <Landmark size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Central FP&A owns the macro base and the central items
          </h2>
        </div>
        <p className="forecast-guide-copy">
          <b>Central FP&A owns the cycle's macro base</b> — SOFR, GDP, deposit beta, credit loss, deposit runoff, and
          expense/wage inflation — plus the <b>centrally planned items</b> every unit inherits (FDIC assessment bps,
          FF&E %, and similar allocated lines). Liaisons never re-key these: each unit's OP_02 scenario starts from the
          central base, and the centrally planned lines flow into every package's NIE automatically.
        </p>
        <p className="forecast-guide-copy">
          These values are edited in section <b>OP_02X "Central macro controls"</b> and <b>OP_02C "Centrally planned
          lines"</b> inside any forecast package page, and the two sections are <b>visible only when acting as
          "Central FP&A"</b> (switch the "Acting as" selector on Cycle home). Saved values propagate to every unit's
          baseline immediately — the next recalc in any open package picks them up, and every console figure downstream
          inherits the same base.
        </p>
        <div className="forecast-guide-cta">
          <Link className="forecast-home-link" to={centralLink || '/forecasting'}>
            <SlidersHorizontal size={14} /> Open the central assumptions module (OP_02X)
          </Link>
          <span className="forecast-health-chip neutral">
            {centralLink === null
              ? 'Resolving the first package…'
              : centralLink === '/forecasting'
                ? 'Package list unavailable — link opens Cycle home'
                : 'Opens the first package in the cycle'}
          </span>
        </div>
        <div className="forecast-guide-note">
          The OP_02X and OP_02C controls render inside the package page only for the <b>Central FP&A</b> role — if you
          land there and don't see them, set "Acting as" to <b>Central FP&A — Corporate Planning</b> on Cycle home
          first. All central edits are lineage-tracked like any other assumption change.
        </div>
      </section>

      {/* ── GD_05 · Driver-to-line-item map ── */}
      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">GD_05 ● Driver-to-line-item map</span>
          <h2>
            <Table2 size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Which drivers move which line items
          </h2>
        </div>
        <p className="forecast-guide-copy">
          <span className="forecast-guide-dot-direct">●</span> direct effect &nbsp;·&nbsp;{' '}
          <span className="forecast-guide-dot-indirect">○</span> indirect (flows through balances/averages) &nbsp;·&nbsp;
          columns are the exact GL accounts each package stages (17 lines × 60 months). Balance accounts load as{' '}
          <b>EOP</b>; income-statement accounts load as <b>MTD</b>; all values load as <b>positive magnitudes</b>.
        </p>
        <div className="forecast-home-table-wrap">
          <table className="forecast-home-table forecast-guide-matrix">
            <thead>
              <tr>
                <th>Driver (where you set it)</th>
                {MATRIX_COLUMNS.map((col) => (
                  <th className="c" key={col.key}>
                    {col.label}
                    <span className="col-sub">{col.sub}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MATRIX_ROWS.map((row) => (
                <tr key={row.driver}>
                  <td>
                    <strong>{row.driver}</strong>
                    <span className="forecast-home-market"> ({row.where})</span>
                  </td>
                  {MATRIX_COLUMNS.map((col) => (
                    <MatrixCell cell={row.cells[col.key]} key={col.key} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="forecast-guide-copy" style={{ marginTop: 10, fontSize: '12.5px' }}>
          Tax (909) is computed as <code>max(0, pre-tax income) × tax rate</code>, so every driver above reaches it
          indirectly. NIE detail loads across the expense mix: 609 Personnel Comp (55%), 619 Benefits (12%), 659
          Professional Services (10%), 709 Business Development (4%), 759 T&E (3%), 779 Community Investment (2%), 789
          Other Operating (14%). Deposits load across 219 Transaction (35%), 229 Savings (45%), 239 Time (20%).
        </p>
      </section>

      {/* ── GD_06 · Outputs ── */}
      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">GD_06 ● Where your numbers go</span>
          <h2>
            <Send size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            From your package to the consoles and the cube
          </h2>
        </div>
        <div className="forecast-guide-grid">
          <div className="forecast-guide-card">
            <h3>FP&A Review Console</h3>
            <p>
              Status board with FY1 deltas and constraint chips, gap heatmaps vs prior plan (GAAP and FTP bases), AI
              variance reads, and the consolidation flash (headline figure is server-computed, never estimated).
            </p>
          </div>
          <div className="forecast-guide-card">
            <h3>Executive Review Console</h3>
            <p>
              Plan bridges by year, EOP balance trends with the working-plan overlay (your saved balance path +
              prior-plan fill for units not yet saved), top movers with your own narratives, and company guardrails.
            </p>
          </div>
          <div className="forecast-guide-card">
            <h3>Forecast cube (GL_Fact)</h3>
            <p>
              On FP&A load: 17 GL lines × 60 months under a deterministic version name (e.g.{' '}
              <code>MARKET_SUBMITTED_COMMERCIAL_BANKING_ATLANTA_BASE</code>), idempotent delete-and-replace, full audit
              row per batch.
            </p>
          </div>
        </div>
        <div className="forecast-guide-note">
          <b>Conventions that keep everything tied:</b> planning versions store <b>positive magnitudes</b> for every
          line (net income = NII + fees − expense − provision − tax); balances load as point-in-time EOP and are never
          summed across months; flows load as MTD and always sum; the funding benefit loads explicitly as account 4290
          so a loaded version's net income equals the package's net income to the penny.
        </div>
      </section>

      <p className="forecast-guide-copy" style={{ marginTop: 18, fontSize: '12.5px' }}>
        RFM guide · maintained alongside the module tie-out audit · <Link to="/forecasting">open the Forecast Console</Link>
      </p>
    </div>
  );
};

export default ForecastGuide;
